import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class DeleteEmailCycleRPC implements
  LeoRPC<DeleteEmailCycleRPC.Request, DeleteEmailCycleRPC.Response, DeleteEmailCycleRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteEmailCycleRPC.Request
  ): Promise<LeoRPCResult<DeleteEmailCycleRPC.Response, DeleteEmailCycleRPC.Errors.Errors>>;
}

export namespace DeleteEmailCycleRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace Scope {
      export enum Scope {
        ONLY_THIS = "ONLY_THIS",
        THIS_AND_PENDING = "THIS_AND_PENDING",
      }
    
      export interface ScopeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: ScopeDTO): Scope => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestScopeError(
            `Invalid DTO ${dto} provided for Scope.Scope`
          );
        }
        switch (dto.case) {
          case "ONLY_THIS":
            return Scope.ONLY_THIS;
          case "THIS_AND_PENDING":
            return Scope.THIS_AND_PENDING;
          default:
            throw new InvalidRequestScopeError(
              `Case ${dto.case} is not valid case of Scope.Scope`
            );
        }
      };
    
      export const toDTO = (scope: Scope): ScopeDTO => {
        const enumDTO = {
          case: Scope[scope],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    scheduleId: string;
    scope: RequestEnums.Scope.ScopeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteEmailCycleRPCRequest: boolean = true;
  
    readonly scheduleId: LeoUUID;
    readonly scope: RequestEnums.Scope.Scope;
  
  
    constructor(
    scheduleId: LeoUUID, 
    scope: RequestEnums.Scope.Scope
    ) {
      this.scheduleId = scheduleId;
      this.scope = scope;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const scheduleId = getLeoUuid(dto, "scheduleId", InvalidRequestScheduleIdError);
      const scope = RequestEnums.Scope.fromDTO(getObject(dto, "scope", InvalidRequestScopeError));
      return new Request(
        scheduleId,
        scope
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          scheduleId: this.scheduleId.toDTO(),
          scope: RequestEnums.Scope.toDTO(this.scope)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteEmailCycleRPC(scheduleId='${this.scheduleId}',scope='${this.scope}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteEmailCycleRPC.Response()`;
  };

  export enum RPCError {
    InvalidScheduleId = "INVALID_SCHEDULE_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    ScheduleInProgress = "SCHEDULE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidScheduleIdDTO {
      code: string;
    }
    
    export class InvalidScheduleId extends LeoRPCError<RPCError> {
      readonly isInvalidScheduleId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidScheduleId;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleIdDTO {
        const dto: InvalidScheduleIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidScheduleId.prototype.toString = function toString(): string {
      return `DeleteEmailCycleRPC.INVALID_SCHEDULE_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteEmailCycleRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface ScheduleInProgressDTO {
      code: string;
    }
    
    export class ScheduleInProgress extends LeoRPCError<RPCError> {
      readonly isScheduleInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ScheduleInProgress;
        super(code);
      }
    
    
      toDTO(): ScheduleInProgressDTO {
        const dto: ScheduleInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ScheduleInProgress.prototype.toString = function toString(): string {
      return `DeleteEmailCycleRPC.SCHEDULE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidScheduleId | ProjectAlreadyArchived | ScheduleInProgress;
    export type ErrorsDTO = InvalidScheduleIdDTO | ProjectAlreadyArchivedDTO | ScheduleInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidScheduleId:
          return new InvalidScheduleId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.ScheduleInProgress:
          return new ScheduleInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestScheduleIdError extends DeleteEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleIdError";
  }
}

export class InvalidRequestScopeError extends DeleteEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeError";
  }
}

export class InvalidRequestScopeOnlyThisError extends InvalidRequestScopeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeOnlyThisError";
  }
}

export class InvalidRequestScopeThisAndPendingError extends InvalidRequestScopeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScopeThisAndPendingError";
  }
}


export class InvalidResponseInvalidScheduleIdError extends DeleteEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseScheduleInProgressError extends DeleteEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseScheduleInProgressError";
  }
}

